import { Input } from "@hostingbv/lib/src/components/form/Input";
import { FC, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { config } from "../config";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { Card } from "../../../lib/src/components/Card";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { HistoricDomainPrice } from "../../../lib/src/api/partner/models/HistoricDomainPrice";
import { ReactiveWidthContainer } from "../../../lib/src/components/ReactiveWidthContainer";
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { faArrowTrendUp } from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";

export const DomainPrices: FC = () => {
  const { partnerClient } = useApiClient(config)
  const { resource, loading, reload } = useFetchedResource(() => partnerClient.getDomainPriceTlds())
  useRefreshEffect(() => { reload() })
  const [search, setSearch] = useState('')
  const [selectedTld, setSelectedTld] = useState<string | null>(null)
  const filteredTlds = useMemo(() => {
    return (resource ?? []).filter(tld => {
      if (search === '') return true
      if (tld === selectedTld) return true
      return tld.toLowerCase().includes(search.toLowerCase())
    })
  }, [resource, selectedTld, search])
  useEffect(() => {
    if (resource !== null && resource.length > 0 && selectedTld === null) {
      setSelectedTld(resource[0])
    }
  }, [selectedTld, resource])
  return (
    <ContentContainer size={'lg'}>
      <PageIconHeader icon={faArrowTrendUp}>
        <Breadcrumbs crumbs={[]} currentPage={"Domain prices"} />
        <PageHeader>Domain Prices</PageHeader>
      </PageIconHeader>
      {/*<DomainOverview domains={domains} loading={loading} reloadDomains={() => reload()} />*/}
      <Loading loading={loading}>
        <Card title={'Domain prices per TLD'}>
          <div className={"flex items-stretch"}>
            <div className={"w-64 border-r border-slate-200 flex flex-col items-stretch h-[800px] overflow-y-scroll -my-4 -ml-6"}>
              <div className={"px-4 bg-slate-50 pt-2 pb-3 border-b border-slate-200"}>
                <Input type={'text'} label={''} value={search} onChange={setSearch} placeholder={'Search'} />
              </div>
              {filteredTlds.map((tld, i) => <button key={i} className={`text-left px-6 py-2 cursor-pointer ${selectedTld === tld ? 'bg-brand-100 text-brand-800' : ''}`} onClick={() => setSelectedTld(tld)}>
                .{tld}
              </button>)}
            </div>
            <div className={"flex-1"}>
              {selectedTld && <DomainPriceHistory tld={selectedTld} />}
            </div>
          </div>
        </Card>
      </Loading>
    </ContentContainer>
  );
}
const DomainPriceHistory: FC<{ tld: string }> = ({ tld }) => {
  const { partnerClient } = useApiClient(config)
  const [, setIsLoading] = useState(false)
  const [history, setHistory] = useState<HistoricDomainPrice[]>([])
  useEffect(() => {
    setIsLoading(true)
    partnerClient.getDomainPriceTldHistory(tld).then((response) => {
      setHistory(response.success ? response.data : [])
      setIsLoading(false)
    })
  }, [tld]);
  type DataPoint = { date: Date } & { [provider: string]: number }
  const ungroupedData: DataPoint[] = useMemo(() => {
    const lastPrice = new Map<string, HistoricDomainPrice>()
    const historicPrices = history.map((item) => {
      if (lastPrice.has(item.provider)) {
        const last = lastPrice.get(item.provider)!
        if (last.date.getTime() < item.date.getTime()) {
          lastPrice.set(item.provider, item)
        }
      } else {
        lastPrice.set(item.provider, item)
      }
      return {
        date: item.date,
        [item.provider]: item.price / 100,
      }
    })
    return ([...historicPrices, {
      date: new Date(),
      ...[...lastPrice.entries()].reduce((acc, [key, value]) => {
        return {
          ...acc,
          [key]: value.price / 100,
        }
      }, {} as Record<string, number>)
    }] as DataPoint[]).sort((a, b) => a.date.getTime() - b.date.getTime())
  }, [history])

  const data: DataPoint[] = useMemo(() => {
    const dateToKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
    const grouped: { [dateKey: string]: DataPoint[] } = ungroupedData.reduce((acc, item) => {
      const key = dateToKey(item.date)
      if (acc[key] === undefined) {
        acc[key] = [item]
      } else {
        acc[key].push(item)
      }
      return acc
    }, {} as { [dateKey: string]: DataPoint[] })
    return Object.entries(grouped).map(([, items]) => {
      const providerPrices = items.reduce((acc, item) => {
        Object.entries(item).forEach(([key, value]) => {
          if (key === 'date') return
          if (acc[key] === undefined) {
            acc[key] = [value as number]
          } else {
            acc[key].push(value as number)
          }
        })
        return acc
      }, {} as { [key: string]: number[] })
      return {
        date: items[0].date,
        ...Object.entries(providerPrices).reduce((acc, [provider, prices]) => {
          return {
            ...acc,
            [provider]: prices.reduce((acc, price) => Math.max(acc, price), 0),
          }
        }, {} as Record<string, number>)
      } as DataPoint
    })
  }, [ungroupedData])



  const uniqueProviders = useMemo(() => {
    return history.reduce((acc, item) => {
      if (acc.includes(item.provider)) return acc
      return [...acc, item.provider]
    }, [] as string[]).sort()
  }, [history])
  const getColor = (i: number): string => {
    const colors = ['#dc2626', '#ea580c', '#16a34a', '#059669', '#0891b2', '#2563eb', '#7c3aed', '#ec4899', '#f43f5e', '#f87171', '#fbbf24', '#f97316', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777', '#dc2626', '#f87171', '#fbbf24', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777', '#dc2626', '#f87171', '#fbbf24', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777', '#dc2626', '#f87171', '#fbbf24', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777', '#dc2626', '#f87171', '#fbbf24', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777', '#dc2626', '#f87171', '#fbbf24', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777', '#dc2626', '#f87171', '#fbbf24', '#f59e0b', '#10b981', '#065f46', '#047857', '#0284c7', '#064e3b', '#1d4ed8', '#7c3aed', '#db2777'];
    return colors[i % colors.length]
  }
  return <div>
    <ReactiveWidthContainer builder={(width) => {
      return <LineChart height={500} width={width} data={data}>
        <XAxis dataKey="date" tickFormatter={(v: Date) => v.toLocaleDateString('nl', {})} />
        <YAxis />
        <Tooltip />
        <Legend />
        <CartesianGrid stroke="#f5f5f5" />
        {uniqueProviders.map((provider, i) => <Line type="monotone" key={i} dataKey={provider} connectNulls={true} stroke={getColor(i)} unit={'€'} strokeWidth={provider === 'windhoos' ? 5 : 2} yAxisId={0} />)}
      </LineChart>
    }} />
  </div>
}
